/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module ItemsModule
 */

import { BANNER_SEVERITY, IControllerBanner } from 'generated-types';
import { Item } from 'item';

/**
 * Severity levels for banner alerts.
 */
export enum ControllerBannerAlertSeverity {
    ERROR = 'ERROR',
    WARN = 'WARN',
    INFO = 'INFO',
}

interface IControllerBannerExtended extends IControllerBanner {
    service: string;
}

/**
 * Interface for banner alert data structure from API.
 */
interface IControllerBannerAlertData {
    config: IControllerBannerExtended;
}

/**
 * Map API severity values to ControllerBannerAlertSeverity.
 */
const SEVERITY_MAP: Record<string, ControllerBannerAlertSeverity> = {
    ERROR: ControllerBannerAlertSeverity.ERROR,
    WARN: ControllerBannerAlertSeverity.WARN,
    INFO: ControllerBannerAlertSeverity.INFO,
    [BANNER_SEVERITY.BANNER_SEVERITY_ERROR]: ControllerBannerAlertSeverity.ERROR,
    [BANNER_SEVERITY.BANNER_SEVERITY_WARN]: ControllerBannerAlertSeverity.WARN,
    [BANNER_SEVERITY.BANNER_SEVERITY_INFO]: ControllerBannerAlertSeverity.INFO,
};

/**
 * Map severity from API to ControllerBannerAlertSeverity for UI.
 */
function mapBannerSeverity(apiSeverity?: BANNER_SEVERITY | string): ControllerBannerAlertSeverity {
    if (!apiSeverity) {
        return ControllerBannerAlertSeverity.ERROR;
    }

    return SEVERITY_MAP[apiSeverity.toUpperCase()] ?? ControllerBannerAlertSeverity.ERROR;
}

/**
 * @description Banner Alert Item for inventory banner API.
 * @author Shubham Bindal
 */
export class ControllerBannerAlertItem extends Item<IControllerBannerExtended> {
    public getConfig!: () => IControllerBannerExtended;
    public data!: IControllerBannerAlertData;

    constructor(args = {}) {
        const extendedArgs = {
            objectName: 'inventory/banner',
            ...args,
        };

        super(extendedArgs);
    }

    /**
     * Get alert service type.
     */
    public get service(): string {
        return this.getConfig()?.service ?? '';
    }

    /**
     * Get alert name.
     */
    public get alertName(): string {
        return this.getConfig()?.name ?? '';
    }

    /**
     * Get alert description/message.
     */
    public get description(): string {
        return this.getConfig()?.description ?? '';
    }

    /**
     * Get alert severity.
     */
    public get severity(): ControllerBannerAlertSeverity {
        return mapBannerSeverity(this.getConfig()?.severity);
    }

    /** @override */
    // eslint-disable-next-line no-underscore-dangle
    protected getIdFromData_(data: IControllerBannerAlertData): string {
        const { config } = data;

        return config ? `${config.name ?? ''}-${config.service}-${config.description ?? ''}` : '';
    }
}
